/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Pill, Calendar, User, RefreshCw, Info } from 'lucide-react';
import { getActiveTherapy } from '@/lib/activeTherapy';

const formatDT = (iso) => {
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso || ''; }
};

/**
 * Side panel that lists the patient's current active therapy. Meant to sit
 * next to a NEW prescription form so the doctor can spot duplicates and
 * potential conflicts before they even type.
 *
 * Props
 *   patientId   the linked patient (null when no patient is selected yet)
 *   onLoaded    optional callback: receives the list of active principles
 */
export const ActiveTherapyPanel = ({ patientId, onLoaded }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    if (!patientId) { setItems([]); onLoaded?.([]); return; }
    setLoading(true);
    try {
      const res = await getActiveTherapy(patientId);
      const list = res.items || [];
      setItems(list);
      onLoaded?.(list.map((x) => (x.active || '').toLowerCase()).filter(Boolean));
    } catch (e) {
      setItems([]); onLoaded?.([]);
    } finally { setLoading(false); }
  }, [patientId, onLoaded]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="active-therapy-panel" className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Pill className="h-4 w-4 text-primary" />
          <CardTitle className="text-sm font-display">Terapia in corso</CardTitle>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading || !patientId}
                  className="ml-auto h-7 px-2" data-testid="active-therapy-refresh">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <CardDescription className="text-[10.5px]">
          Ultimi 90 giorni · ricette firmate/trasmesse
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {!patientId && (
          <div className="text-[11px] text-muted-foreground flex items-start gap-1.5">
            <Info className="h-3 w-3 mt-0.5" />
            Seleziona un paziente per vedere la sua terapia.
          </div>
        )}
        {patientId && loading && [...Array(3)].map((_, i) => (
          <Skeleton key={`therapy-skel-${i}`} className="h-14 rounded-md" />
        ))}
        {patientId && !loading && items.length === 0 && (
          <div className="text-[11px] text-muted-foreground">Nessuna terapia attiva registrata.</div>
        )}
        {items.map((d) => (
          <div key={d.doc_id + (d.aic || '')} className="rounded-md border border-border p-2" data-testid={`active-therapy-item-${d.active}`}>
            <div className="flex items-center gap-1.5">
              <Badge variant="secondary" className="text-[10px] uppercase">{d.active}</Badge>
              {d.aic && <span className="text-[9px] text-muted-foreground font-mono">AIC {d.aic}</span>}
            </div>
            <div className="text-xs font-medium truncate mt-1">{d.name}</div>
            <div className="text-[10.5px] text-muted-foreground line-clamp-2">{d.posology}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-0.5">
                <Calendar className="h-2.5 w-2.5" /> {formatDT(d.prescribed_at)}
              </span>
              {d.prescriber_name && (
                <span className="flex items-center gap-0.5">
                  <User className="h-2.5 w-2.5" /> {d.prescriber_name}
                </span>
              )}
              {d.nre && (
                <span className="font-mono truncate">NRE {String(d.nre).slice(0,10)}…</span>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default ActiveTherapyPanel;
